import React from 'react';
import { conferirDetalhe, proximaPendente, totaisDaLinha } from '../../utils/contabil/conferencia';
import {
  TEXTO, SUAVE, BORDA, VERDE, AMBAR, VERMELHO, ROXO, fmtMoeda, fmtData, fmtDataHora,
  botaoPrimario, botaoSecundario, botaoPequeno, th, td, desabilitado, SeloSituacao,
} from './comum';

// Etapa 3 — Conferir as contas do balancete do mês, uma a uma, em painel lateral.
// Lista à esquerda; à direita o detalhe com saldo anterior, cada lançamento,
// saldo final, observação e os botões Validar / Desfazer / Próxima conta.

const COR_FLAG = { erro: VERMELHO, alerta: AMBAR, info: '#1d4ed8' };

export default function EtapaConferir({
  itens, totalDebitos, totalCreditos, aClassificar, mesFechado, nomeMes,
  fechamento, codigosAntigos, onIrEtapa, setAviso, cardStyle, inputStyle, labelStyle,
}) {
  const [selecionada, setSelecionada] = React.useState(null);
  const [soPendentes, setSoPendentes] = React.useState(false);
  const [observacao, setObservacao] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [confirmarAntigas, setConfirmarAntigas] = React.useState(false);

  const visiveis = soPendentes ? itens.filter(i => i.situacao !== 'validada') : itens;

  // Seleção inicial (e quando a conta some do balancete): primeira não validada.
  React.useEffect(() => {
    if (selecionada && itens.some(i => i.codigo === selecionada)) return;
    const primeira = itens.find(i => i.situacao !== 'validada') || itens[0];
    setSelecionada(primeira ? primeira.codigo : null);
  }, [itens, selecionada]);

  const atual = itens.find(i => i.codigo === selecionada) || null;

  React.useEffect(() => {
    setObservacao((atual && atual.validacao && atual.validacao.observacao) || '');
    // Só ao trocar de conta: não sobrescreve o que está sendo digitado a cada recarga.
  }, [selecionada]); // eslint-disable-line react-hooks/exhaustive-deps

  const conferencia = atual ? conferirDetalhe(atual.linha, atual.lancs) : null;
  const proxima = atual ? proximaPendente(itens, atual.codigo) : null;

  const acao = async (tipo) => {
    if (!atual || salvando) return;
    setSalvando(true);
    setAviso(null);
    const r = tipo === 'validar'
      ? await fechamento.validar(atual.codigo, { observacao, totais: totaisDaLinha(atual.linha) })
      : await fechamento.desfazer(atual.codigo, { observacao });
    setSalvando(false);
    if (!r.ok) {
      setAviso({ tipo: 'erro', texto: tipo === 'validar' ? 'Não foi possível validar a conta. Tente novamente.' : 'Não foi possível desfazer a validação. Tente novamente.' });
    }
  };

  const trazerAntigas = async () => {
    const porCodigo = new Map(itens.map(i => [i.codigo, i]));
    const lista = codigosAntigos.filter(c => porCodigo.has(c)).map(c => ({ conta: c, totais: totaisDaLinha(porCodigo.get(c).linha) }));
    if (!lista.length) return;
    setSalvando(true);
    const r = await fechamento.importarAntigas(lista);
    setSalvando(false);
    setConfirmarAntigas(false);
    setAviso(r.ok
      ? { tipo: 'sucesso', texto: `${lista.length} validação(ões) trazida(s) do Balancete. Confira as contas com alerta antes de fechar.` }
      : { tipo: 'erro', texto: 'Não foi possível trazer as validações anteriores. Tente novamente.' });
  };

  const validadas = itens.filter(i => i.situacao === 'validada').length;
  const antigasAplicaveis = codigosAntigos.filter(c => itens.some(i => i.codigo === c));
  const mostrarAntigas = !fechamento.carregando && fechamento.validacoes.length === 0 && antigasAplicaveis.length > 0 && !mesFechado;
  const diferenca = Math.round((totalDebitos - totalCreditos) * 100) / 100;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h4 style={{ color: TEXTO, margin: 0 }}>Conferência das contas — {nomeMes}</h4>
        <span style={{ fontSize: 12, color: SUAVE }}>
          {validadas} de {itens.length} validada(s) · Débitos {fmtMoeda(totalDebitos)} · Créditos {fmtMoeda(totalCreditos)}
          {Math.abs(diferenca) > 0 && <strong style={{ color: VERMELHO }}> · Diferença {fmtMoeda(diferenca)}</strong>}
        </span>
      </div>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
        Todas as contas com saldo ou movimento no mês. Confira os lançamentos de cada uma e valide.
      </p>

      {aClassificar > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: AMBAR, marginBottom: 12 }}>
          Há {aClassificar} lançamento(s) em A Classificar no mês; eles mudam os saldos das contas.
          <button onClick={() => onIrEtapa(2)} style={botaoPequeno}>Classificar</button>
        </div>
      )}

      {mostrarAntigas && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.35)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1d4ed8' }}>
          <span>{antigasAplicaveis.length} conta(s) deste mês foram validadas na tela do Balancete antes do assistente.</span>
          {confirmarAntigas ? (
            <>
              <button onClick={trazerAntigas} disabled={salvando} style={desabilitado({ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }, salvando)}>Confirmar</button>
              <button onClick={() => setConfirmarAntigas(false)} style={botaoPequeno}>Cancelar</button>
            </>
          ) : (
            <button onClick={() => setConfirmarAntigas(true)} style={botaoPequeno}>Trazer validações anteriores</button>
          )}
        </div>
      )}

      {itens.length === 0 ? (
        <div style={{ fontSize: 13, color: SUAVE }}>Nenhuma conta com saldo ou movimento neste mês.</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Lista de contas */}
          <div style={{ flex: '1 1 280px', maxWidth: 400, minWidth: 0 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: TEXTO, marginBottom: 8 }}>
              <input type="checkbox" checked={soPendentes} onChange={e => setSoPendentes(e.target.checked)} />
              Só pendentes
            </label>
            <div role="listbox" aria-label="Contas do balancete" style={{ maxHeight: 560, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
              {visiveis.length === 0 && <div style={{ padding: 12, fontSize: 12, color: VERDE }}>Todas as contas estão validadas.</div>}
              {visiveis.map(i => {
                const ativa = i.codigo === selecionada;
                return (
                  <button
                    key={i.codigo}
                    role="option"
                    aria-selected={ativa}
                    onClick={() => setSelecionada(i.codigo)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none',
                      borderBottom: '1px solid #f1eef7', borderLeft: `3px solid ${ativa ? ROXO : 'transparent'}`,
                      background: ativa ? 'rgba(124,58,237,0.08)' : '#ffffff', padding: '8px 10px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: SUAVE, fontWeight: 600 }}>{i.codigo}</span>
                      <SeloSituacao situacao={i.situacao} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 13, color: TEXTO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.linha.nome}</span>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: TEXTO, whiteSpace: 'nowrap' }}>{fmtMoeda(i.linha.saldoFinal)}</span>
                    </div>
                    {i.flag && (
                      <div style={{ fontSize: 11, color: COR_FLAG[i.flag.tipo] || SUAVE, marginTop: 2 }}>{i.flag.msg}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detalhe da conta */}
          {atual && (
            <div style={{ flex: '2 1 440px', minWidth: 0, border: `1px solid ${BORDA}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <h5 style={{ margin: 0, fontSize: 15, color: TEXTO }}>{atual.codigo} — {atual.linha.nome}</h5>
                <SeloSituacao situacao={atual.situacao} />
              </div>
              {atual.validacao && atual.validacao.status === 'validado' && (
                <div style={{ fontSize: 11, color: SUAVE, marginTop: 4 }}>
                  Validada em {fmtDataHora(atual.validacao.validado_em)}
                  {atual.situacao === 'desatualizada' && <span style={{ color: VERMELHO }}> · Alterada após a validação: confira e valide novamente.</span>}
                </div>
              )}
              {atual.flag && (
                <div style={{ fontSize: 12, color: COR_FLAG[atual.flag.tipo] || SUAVE, marginTop: 6 }}>{atual.flag.msg}</div>
              )}

              <div style={{ maxHeight: 380, overflow: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8, marginTop: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Data</th>
                      <th style={th}>Histórico</th>
                      <th style={th}>Contrapartida</th>
                      <th style={{ ...th, textAlign: 'right' }}>Débito</th>
                      <th style={{ ...th, textAlign: 'right' }}>Crédito</th>
                      <th style={th}>Origem</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={3}><strong>Saldo anterior</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }} colSpan={2}>{fmtMoeda(atual.linha.saldoAnterior)}</td>
                      <td style={td} />
                    </tr>
                    {atual.lancs.length === 0 && (
                      <tr><td style={{ ...td, color: SUAVE }} colSpan={6}>Sem movimento no mês; só saldo anterior.</td></tr>
                    )}
                    {atual.lancs.map((lc, k) => (
                      <tr key={`${lc.dbId || lc.lancIdx}-${lc.lado}-${k}`}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(lc.data)}</td>
                        <td style={td}>{lc.historico}</td>
                        <td style={{ ...td, color: SUAVE }}>{lc.contrapartida || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{lc.lado === 'D' ? fmtMoeda(lc.valor) : ''}</td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{lc.lado === 'C' ? fmtMoeda(lc.valor) : ''}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: SUAVE }}>{lc.fonte || lc.origem}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={3}><strong>Movimento do mês</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMoeda(atual.linha.debitos)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMoeda(atual.linha.creditos)}</td>
                      <td style={td} />
                    </tr>
                    <tr style={{ background: '#f7f5fb' }}>
                      <td style={td} colSpan={3}><strong>Saldo final</strong></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }} colSpan={2}>{fmtMoeda(atual.linha.saldoFinal)}</td>
                      <td style={td} />
                    </tr>
                  </tbody>
                </table>
              </div>

              {conferencia && (
                <div style={{ fontSize: 12, marginTop: 8, color: conferencia.confere ? VERDE : VERMELHO }}>
                  {conferencia.confere
                    ? 'A soma dos lançamentos confere com a linha do balancete.'
                    : `A soma dos lançamentos não confere com a linha do balancete (débitos ${fmtMoeda(conferencia.debitos)}, créditos ${fmtMoeda(conferencia.creditos)}). Isso ocorre com o encerramento de exercício em dezembro; nos demais casos, acione o suporte.`}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
                <label htmlFor="fm-observacao" style={labelStyle}>Observação</label>
                <textarea
                  id="fm-observacao"
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  disabled={mesFechado}
                  rows={2}
                  placeholder="Opcional. Ex.: conferido com o extrato de 31/05."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {mesFechado ? (
                  <span style={{ fontSize: 12, color: SUAVE }}>O mês está fechado; a validação não pode ser alterada.</span>
                ) : (
                  <>
                    <button onClick={() => acao('validar')} disabled={salvando} style={desabilitado(botaoPrimario, salvando)}>
                      {salvando ? 'Gravando…' : atual.situacao === 'desatualizada' ? 'Validar novamente' : atual.situacao === 'validada' ? 'Salvar observação' : 'Validar'}
                    </button>
                    {atual.validacao && atual.validacao.status === 'validado' && (
                      <button onClick={() => acao('desfazer')} disabled={salvando} style={desabilitado(botaoSecundario, salvando)}>Desfazer validação</button>
                    )}
                  </>
                )}
                <button onClick={() => proxima && setSelecionada(proxima)} disabled={!proxima} style={desabilitado(botaoSecundario, !proxima)}>
                  Próxima conta
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
